import React, { useEffect, useState } from "react";
import { CRMLayout } from "@/components/CRMLayout";
import { api } from "@/lib/api";
import { formatPrice } from "@/components/ListingCard";
import { Plus, X } from "lucide-react";

const STAGES = [
  { v: "prospecting", l: "Prospecting" },
  { v: "offer", l: "Offer" },
  { v: "negotiation", l: "Negotiation" },
  { v: "contract", l: "Contract" },
  { v: "closing", l: "Closing" },
  { v: "closed", l: "Closed" },
];

const TYPE_COLOR = {
  residential: "bg-blue-100 text-blue-800",
  commercial: "bg-slate-100 text-slate-700",
  dental: "bg-emerald-100 text-emerald-800",
  medical: "bg-rose-100 text-rose-800",
};

const Deals = () => {
  const [deals, setDeals] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", type: "residential", value: "", stage: "prospecting", notes: "" });

  const load = () => api.get("/crm/deals").then((r) => setDeals(r.data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/crm/deals", { ...form, value: Number(form.value || 0) });
      setOpen(false);
      setForm({ title: "", type: "residential", value: "", stage: "prospecting", notes: "" });
      load();
    } catch {}
  };

  const onDragStart = (e, id) => e.dataTransfer.setData("id", id);
  const onDrop = async (e, stage) => {
    const id = e.dataTransfer.getData("id");
    if (!id) return;
    await api.patch(`/crm/deals/${id}`, { stage });
    load();
  };

  return (
    <CRMLayout>
      <div className="flex items-end justify-between mb-6" data-testid="crm-deals-page">
        <div>
          <div className="overline text-gold">Pipeline</div>
          <h1 className="font-display text-3xl text-navy mt-2">Deals</h1>
        </div>
        <button onClick={() => setOpen(true)} className="btn-navy text-sm py-2.5 px-4" data-testid="deals-add-btn"><Plus className="w-4 h-4" /> New Deal</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-6 gap-4" data-testid="deals-kanban">
        {STAGES.map((s) => {
          const col = deals.filter((d) => d.stage === s.v);
          const total = col.reduce((a, d) => a + (d.value || 0), 0);
          return (
            <div
              key={s.v}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => onDrop(e, s.v)}
              className="bg-white border border-slate-200 min-h-[400px] flex flex-col"
              data-testid={`deals-col-${s.v}`}
            >
              <div className="p-3 border-b border-slate-200 bg-slate-50">
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-sm text-navy">{s.l}</div>
                  <span className="text-xs bg-gold/20 text-navy px-2 py-0.5 font-semibold">{col.length}</span>
                </div>
                <div className="text-xs text-slate-500 mt-1">{formatPrice(total)}</div>
              </div>
              <div className="kanban-col p-2 space-y-2 flex-1 overflow-y-auto">
                {col.map((d) => (
                  <div
                    key={d.id}
                    draggable
                    onDragStart={(e) => onDragStart(e, d.id)}
                    className="bg-white border border-slate-200 p-3 cursor-move hover:border-gold transition"
                    data-testid={`deal-card-${d.id}`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="font-semibold text-sm text-navy leading-snug">{d.title}</div>
                      <span className={`text-[10px] px-1.5 py-0.5 uppercase tracking-wider ${TYPE_COLOR[d.type] || TYPE_COLOR.residential}`}>{d.type}</span>
                    </div>
                    <div className="text-sm font-display text-navy">{formatPrice(d.value)}</div>
                    {d.notes && <div className="text-xs text-slate-500 mt-2 line-clamp-2">{d.notes}</div>}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {open && (
        <div className="fixed inset-0 bg-navy/70 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white w-full max-w-lg p-8 relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setOpen(false)} className="absolute top-4 right-4 text-slate-400" data-testid="deal-modal-close"><X className="w-5 h-5" /></button>
            <div className="overline text-gold">New</div>
            <h2 className="font-display text-2xl text-navy mt-2 mb-6">Add Deal</h2>
            <form onSubmit={create} className="space-y-3" data-testid="deal-create-form">
              <input required placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full border border-slate-200 px-3 py-2.5 text-sm" data-testid="deal-form-title" />
              <div className="grid grid-cols-2 gap-3">
                <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="border border-slate-200 px-3 py-2.5 text-sm bg-white" data-testid="deal-form-type">
                  <option value="residential">Residential</option><option value="commercial">Commercial</option><option value="dental">Dental</option><option value="medical">Medical</option>
                </select>
                <input required type="number" placeholder="Value ($)" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className="border border-slate-200 px-3 py-2.5 text-sm" data-testid="deal-form-value" />
              </div>
              <select value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })} className="w-full border border-slate-200 px-3 py-2.5 text-sm bg-white" data-testid="deal-form-stage">
                {STAGES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
              </select>
              <textarea rows="3" placeholder="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full border border-slate-200 px-3 py-2.5 text-sm" data-testid="deal-form-notes" />
              <button className="btn-navy w-full" data-testid="deal-form-submit">Create Deal</button>
            </form>
          </div>
        </div>
      )}
    </CRMLayout>
  );
};

export default Deals;
